"use client";

import Image from "next/image";

import { OutboundLink } from "@/features/outbound-link/outbound-link";
import { withBasePath } from "@/lib/base-path";
import { BiEvent } from "@/lib/bi/bi.enum";

import { usePrimeDayCountdown } from "../hooks/use-prime-day-countdown";
import {
  PopupCountdownDigit,
  PopupCountdownSeparator,
} from "./popup-countdown-digit";

type Props = {
  productUrl: string;
  productName: string;
  productImage: string;
  percentOff?: number | null;
  onClickOut: () => void;
};

export function PopupDesktop({
  productUrl,
  productName,
  productImage,
  onClickOut,
}: Props) {
  const { days, hours, minutes, seconds } = usePrimeDayCountdown();

  return (
    <OutboundLink
      url={productUrl}
      storeName="Amazon"
      bi={{
        eventType: BiEvent.BannerOutbound,
        provider: "amazon",
        location: "prime-day-popup",
      }}
      target="_blank"
      rel="noopener noreferrer"
      className="relative flex bg-grayscale-white rounded-2xl overflow-hidden w-[640px] cursor-pointer"
      onClick={onClickOut}
    >
      {/* Left panel — gradient */}
      <div className="flex shrink-0 flex-col gap-6 bg-gradient-to-t from-[#fafdff] from-50% to-[#ddf1ff] rounded-tl-2xl rounded-bl-2xl p-10 overflow-hidden w-[376px]">
        {/* Title + discount */}
        <div className="flex flex-col gap-2 w-[296px]">
          <p className="text-[56px] font-semibold leading-[67px] text-[#2c384a]">
            Prime Day
          </p>
          <div className="flex items-center gap-0.5">
            <span className="text-[80px] font-black leading-[80px] text-[#e03223]">
              50
            </span>
            <div className="flex flex-col items-start justify-center gap-0.5">
              <span className="text-4xl font-extrabold leading-9 text-[#e03223]">
                %
              </span>
              <span className="text-[28px] font-extrabold leading-7 text-[#e03223]">
                OFF
              </span>
            </div>
          </div>
        </div>

        {/* Countdown section */}
        <div className="relative flex flex-col gap-1">
          {/* Bags SVG */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={withBasePath("/prime-day/bags.svg")}
            alt=""
            width={92}
            height={88}
            className="absolute -top-[52px] left-[204px] z-0"
          />
          <p className="text-base text-[#626e79]">Deal ends in</p>
          <div className="relative z-10 flex items-start gap-0.5">
            <PopupCountdownDigit
              value={days}
              label="Days"
              size="desktop"
            />
            <PopupCountdownSeparator size="desktop" />
            <PopupCountdownDigit
              value={hours}
              label="Hours"
              size="desktop"
            />
            <PopupCountdownSeparator size="desktop" />
            <PopupCountdownDigit
              value={minutes}
              label="Min"
              size="desktop"
            />
            <PopupCountdownSeparator size="desktop" />
            <PopupCountdownDigit
              value={seconds}
              label="Sec"
              size="desktop"
            />
          </div>
        </div>

        {/* CTA + Amazon */}
        <div className="flex flex-col items-center gap-4 w-full">
          <div className="w-full rounded-xl bg-cta lg:hover:bg-cta-hover lg:transition-colors py-2.5 text-base font-semibold text-grayscale-white flex items-center justify-center">
            Check Prime day price
          </div>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={withBasePath("/logos/amazon.png")}
            alt="Amazon"
            width={69}
            height={20}
          />
        </div>
      </div>

      {/* Right panel — product */}
      <div
        className="relative flex shrink-0 flex-col items-center justify-center gap-5 px-[52px] py-10 w-[264px]"
        style={{ backgroundColor: "#ffffff" }}
      >
        <div>
          <Image
            src={productImage}
            alt={productName}
            width={160}
            height={160}
            className="object-contain"
          />
        </div>
        <p className="text-sm text-[#2c384a] text-center line-clamp-2">
          {productName}
        </p>
      </div>
    </OutboundLink>
  );
}
